import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Factory, CheckCircle, Shield, Activity, TrendingUp, BarChart3, Play } from 'lucide-react';
import VideoModal from '../components/VideoModal';
import mediaAssets from '../config/mediaAssets';

const Industry4 = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[#EAF6E6] to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <Factory className="w-16 h-16 text-[var(--accent-primary)] mx-auto mb-6" />
            <h1 className="heading-1 mb-6">Make every shift more consistent than the last.</h1>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto mb-8">
              Digitise frontline execution—SOPs, inspections, audits, maintenance—so work is standard, traceable, and continuously improving.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">Talk to an Expert</Link>
              <Link to="/contact" className="btn-secondary">Book a Discovery Call</Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl group cursor-pointer"
            onClick={() => setIsVideoOpen(true)}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={mediaAssets.solutionImages.industry.hero}
              className="w-full h-[500px] object-cover"
            >
              <source src={mediaAssets.videos.industry} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[var(--accent-primary)] flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all">
                <Play className="w-8 h-8 text-white fill-white ml-1" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-2 text-center mb-12">What it enables</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Activity, title: 'Connected visibility', desc: 'Clear, real-time operational picture across sites. Exceptions that surface what matters. Supervisor-ready dashboards.' },
              { icon: Shield, title: 'Safety & compliance', desc: 'SOP-driven checks and inspections. Audit-ready records without paper trails. Consistent execution across teams.' },
              { icon: TrendingUp, title: 'Productivity enablement', desc: 'Guided workflows for field teams. Task tracking and accountability. Smoother handovers between shifts.' },
              { icon: BarChart3, title: 'Analytics', desc: 'Trends and recurring issues. Performance visibility across sites. Early warning signals for operational risk.' }
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="product-card">
                <item.icon className="w-10 h-10 text-[var(--accent-primary)] mb-4" />
                <h3 className="heading-3 mb-3">{item.title}</h3>
                <p className="text-[var(--text-secondary)]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-[var(--bg-section)]">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-2 text-center mb-12">Common use cases</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Routine inspections and audit documentation',
              'Maintenance workflows and proof-of-execution',
              'Quality checks and exception reporting',
              'HSE compliance and corrective action tracking',
              'Onboarding and operator training support'
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="flex items-start gap-3 p-6 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg">
                <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-body)]">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading-2 mb-6">If your operation runs on checklists, handovers, and judgment calls—start here.</h2>
          <Link to="/contact" className="btn-primary">Book a Discovery Call</Link>
        </div>
      </section>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        title="Industry 4.0 Demo"
      />
    </div>
  );
};

export default Industry4;